import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/flowly/PageContainer/PageContainer";
import { cn } from "@/lib/utils";
import { COMPARISONS } from "./data";

/**
 * Per-slug comparison page (TodoAI vs <Competitor>).
 *
 * Why these exist: Princeton's GEO study (KDD 2024) found that comparison
 * articles are ~33% of all ChatGPT / Perplexity citations. A single
 * well-structured "X vs Y" page can earn citations across every variation
 * of that query — and the long tail of "is X better than Y", "X alternatives",
 * "X pricing vs Y" sub-queries that Google's fan-out generates.
 *
 * The visible UI mirrors the schema pattern: definition blocks, a comparison
 * table, cited stats, FAQ. RouteSEO emits matching FAQPage JSON-LD so the
 * structured data and the visible content stay in sync.
 *
 * Unknown slug → redirect to /404 (NotFound) rather than render an empty
 * shell — keeps Google from indexing thin comparison pages.
 */
export default function ComparePage() {
  const { slug } = useParams<{ slug: string }>();
  const data = slug ? COMPARISONS[slug] : undefined;

  if (!data) {
    return <Navigate to="/404" replace />;
  }

  const lastUpdatedLabel = new Date(data.lastUpdated).toLocaleDateString(
    "en-US",
    { year: "numeric", month: "long", day: "numeric" },
  );

  return (
    <PageContainer width="wide" className="py-12 md:py-16 space-y-12">
      {/* Hero — direct answer in the first paragraph. AI engines extract
          this verbatim for "what's the difference between X and Y" queries. */}
      <header className="space-y-4 max-w-4xl">
        <h1 className="text-3xl md:text-5xl font-bold text-foreground leading-tight">
          {data.title}
        </h1>
        <p className="text-sm text-muted-foreground">
          Last updated: {lastUpdatedLabel}
        </p>
      </header>

      {/* Side-by-side definition blocks. 40-60 words each — optimal for
          standalone snippet extraction. */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <article className="rounded-2xl border border-border bg-card p-6 space-y-3">
          <h2 className="text-xl font-bold text-foreground">TodoAI</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {data.introTodoAi}
          </p>
          <Link to={data.todoAiFlowPath}>
            <Button variant="secondary" size="sm" className="gap-2 mt-2">
              Try TodoAI's {data.competitorCategory}
            </Button>
          </Link>
        </article>
        <article className="rounded-2xl border border-border bg-card p-6 space-y-3">
          <h2 className="text-xl font-bold text-foreground">
            {data.competitorName}
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {data.introCompetitor}
          </p>
        </article>
      </section>

      {/* Stats — cited numbers boost LLM citation rate by +37-40% (Princeton). */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {data.stats.map((stat, idx) => (
          <div
            key={idx}
            className="rounded-xl border border-border bg-card p-4 space-y-1"
          >
            <div className="text-2xl font-bold text-primary">{stat.value}</div>
            <p className="text-xs text-muted-foreground leading-snug">
              {stat.description}
            </p>
            {stat.source && (
              <a
                href={stat.source}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-muted-foreground/70 hover:text-foreground inline-flex items-center gap-1"
              >
                {stat.sourceLabel ?? "Source"}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        ))}
      </section>

      {/* The comparison table — AI engines prefer tables over prose for
          "X vs Y" queries. Each row is a self-contained fact-extractable
          unit. */}
      <section className="space-y-4">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground">
          Feature comparison: TodoAI vs {data.competitorName}
        </h2>
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-left">
                <th className="p-4 font-semibold text-foreground w-1/4">
                  Feature
                </th>
                <th className="p-4 font-semibold text-foreground w-3/8">
                  TodoAI
                </th>
                <th className="p-4 font-semibold text-foreground w-3/8">
                  {data.competitorName}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, idx) => (
                <tr key={idx} className="border-t border-border">
                  <td className="p-4 font-medium text-foreground align-top">
                    {row.label}
                  </td>
                  <td
                    className={cn(
                      "p-4 text-muted-foreground align-top",
                      row.highlight === "todoAi" &&
                        "bg-primary/5 text-foreground",
                    )}
                  >
                    <span className="flex items-start gap-2">
                      {row.highlight === "todoAi" && (
                        <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      )}
                      {row.todoAi}
                    </span>
                  </td>
                  <td
                    className={cn(
                      "p-4 text-muted-foreground align-top",
                      row.highlight === "competitor" &&
                        "bg-primary/5 text-foreground",
                    )}
                  >
                    <span className="flex items-start gap-2">
                      {row.highlight === "competitor" && (
                        <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      )}
                      {row.competitor}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ — also emitted as FAQPage JSON-LD by RouteSEO so the same
          questions and answers double as a structured-data feed. */}
      <section className="space-y-4 max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground">
          Frequently asked questions
        </h2>
        <div className="space-y-3">
          {data.faqs.map((faq, idx) => (
            <details
              key={idx}
              className="rounded-xl border border-border bg-card p-4 group"
            >
              <summary className="cursor-pointer font-semibold text-foreground list-none flex items-start justify-between gap-3">
                <span>{faq.question}</span>
                <span className="text-muted-foreground group-open:rotate-180 transition-transform">
                  ▾
                </span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {faq.answer}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA — every comparison ends with the relevant TodoAI flow link.
          Avoids the dead-end pattern that hurts dwell-time signals. */}
      <section className="rounded-2xl border border-primary/30 bg-primary/[0.04] p-6 md:p-8 text-center space-y-4">
        <h2 className="text-2xl font-bold text-foreground">
          Ready to try TodoAI's {data.competitorCategory}?
        </h2>
        <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
          Start free with 100 credits/month — no credit card, no prompt
          engineering, full Hebrew support.
        </p>
        <Link to={data.todoAiFlowPath}>
          <Button size="lg">Open {data.competitorCategory} →</Button>
        </Link>
      </section>
    </PageContainer>
  );
}
